import { groupBy, maxBy, sortBy } from 'lodash';

/**
 * Groups flat categories array by Level 2 (path[0]) for hierarchical display
 * @param {Array} categories - Flat array of category objects
 * @param {string} sortColumn - Column to sort by
 * @param {boolean} sortReverse - Whether to reverse sort order
 * @returns {Array} Array of group objects with children
 */
export const groupCategoriesByLevel2 = (categories, sortColumn = 'uniqueness-index', sortReverse = true) => {
  if (!categories || !categories.length) {
    return [];
  }

  // Group by path[0] (Level 2 category name)
  // Use path[0] as group key, fallback to phrase if path is empty
  const getGroupKey = (cat) => (cat.path && cat.path.length > 0 ? cat.path[0] : cat.phrase || 'Other');
  const grouped = groupBy(categories, getGroupKey);

  // Transform to array of group objects
  const groupsArray = Object.entries(grouped).map(([groupName, children]) => {
    // Find child with highest value for the sorted column to determine group's sort value
    const maxValChild =
      maxBy(children, (c) => {
        const val = c[sortColumn];
        return typeof val === 'number' ? val : 0;
      }) || children[0];

    let sortedChildren = sortBy(children, (c) => {
      const val = c[sortColumn];
      if (typeof val === 'number') {
        return sortReverse ? -val : val;
      }
      return (val || '').toString().toLowerCase();
    });

    if (sortReverse && typeof children[0]?.[sortColumn] !== 'number') {
      sortedChildren.reverse();
    }

    return {
      id: `group-${groupName}`,
      groupName,
      isGroup: true,
      children: sortedChildren,
      // Group header metrics from max value child or the child itself
      'uniqueness-index': maxValChild['uniqueness-index'] || 0,
      'interest-portion': maxValChild['interest-portion'] || 0,
      'segment-portion': maxValChild['segment-portion'] || 0,
      reach: maxValChild.reach || 0,
      categoryId: maxValChild.id, // Group row needs a representative ID for selection context if needed
      phrase: groupName,
      // Group header should have empty reach and id in UI but we need them for sorting if clicked
    };
  });

  // Sort groups by max value child's value in that column
  let sortedGroups = sortBy(groupsArray, (g) => {
    const val = g[sortColumn] || (g.children[0] && g.children[0][sortColumn]);
    if (typeof val === 'number') {
      return sortReverse ? -val : val;
    }
    return (val || '').toString().toLowerCase();
  });

  if (sortReverse && typeof groupsArray[0]?.[sortColumn] !== 'number') {
    sortedGroups.reverse();
  }

  return sortedGroups;
};

/**
 * Filters categories by search term across category names, taxonomy paths, and DMP IDs
 * @param {Array} categories - Flat array of category objects
 * @param {string} searchTerm - Search string
 * @returns {Array} Filtered categories array
 */
export const filterCategoriesBySearch = (categories, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) {
    return categories;
  }

  const term = searchTerm.toLowerCase().trim();

  return categories.filter((cat) => {
    // Search in phrase (category name)
    if (cat.phrase && cat.phrase.toLowerCase().includes(term)) {
      return true;
    }

    // Search in full taxonomy path
    if (cat.path && cat.path.some((p) => p.toLowerCase().includes(term))) {
      return true;
    }

    // Search in DMP Category ID
    if (cat.id && String(cat.id).includes(term)) {
      return true;
    }

    return false;
  });
};

/**
 * Gets the full taxonomy path as a formatted string
 * @param {Object} category - Category object with path array
 * @returns {string} Formatted taxonomy path string
 */
export const getFullTaxonomyPath = (category) => {
  if (!category.path || category.path.length === 0) {
    return '';
  }
  return category.path.join(' > ');
};

/**
 * Gets the display name for a category (last element of path or phrase)
 * @param {Object} category - Category object
 * @returns {string} Display name
 */
export const getCategoryDisplayName = (category) => {
  if (category.path && category.path.length > 0) {
    return category.path[category.path.length - 1];
  }
  return category.phrase || '';
};
